// PushNotificationHandler.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import VisibilityManager from '../resource/VisibilityManager.js';
import { getIsSafari } from '../resource/getUserAgent.js';
import { pushNotification as pushNotificationLog } from '../resource/debug.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';
import { getSentry } from '../resource/sentry.js';

const log = pushNotificationLog.extend('log');

const isSafari = getIsSafari();

export const PushNotificationHandler = ({
  isAuthed = false,
  fcmToken = '',
  pushyToken = '',
  permission = '',
  pushyAppId = '',
  submitPushNotificationToken = NULL_FUNCTION,
  setFcmToken = NULL_FUNCTION,
  setPushyToken = NULL_FUNCTION,
  push = NULL_FUNCTION,
}) => {
  const prevPermission = useRef(permission);
  const prevPushyAddId = useRef(pushyAppId);
  const visibilityManager = useRef(null);

  const shouldSetFcmToken = () =>
    window.PushManager && permission === 'granted';

  const shouldSetPushyToken = () => permission !== 'denied';

  const handlePermission = () => {
    log('permission changed', {
      prevPermission: prevPermission.current,
      permission,
    });
    if (prevPermission.current !== permission && permission === 'granted') {
      if (shouldSetFcmToken()) {
        log('get the new fcm token');
        setFcmToken();
      }
      if (shouldSetPushyToken()) {
        log('get the new pushy token');
        setPushyToken();
      }
    }
    prevPermission.current = permission;
  };

  const handlePushNotificationReceivedEvents = useCallback(async () => {
    let swPersist;
    try {
      swPersist = await import('../serviceWorker/persist.js');
      if (swPersist) {
        const { getItem, NOTIFICATION_RECEIVED_KEY } = swPersist;
        const receivedEvents = await getItem(NOTIFICATION_RECEIVED_KEY);
        if (receivedEvents?.length) {
          receivedEvents.forEach(event => {
            trackMixpanel({
              type: EventTypes.PUSH_NOTIFICATION_RECEIVED,
              payload: event,
            });
          });
        }
      }
    } catch (error) {
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setTag('record', 'push-notification-received-events');
          captureMessage(error);
        });
      });
    } finally {
      if (swPersist) {
        const { removeItem, NOTIFICATION_RECEIVED_KEY } = swPersist;
        await removeItem(NOTIFICATION_RECEIVED_KEY);
      }
    }
  }, []);

  const handlePushNotificationClickedEvent = useCallback(async () => {
    let swPersist;
    try {
      swPersist = await import('../serviceWorker/persist.js');
      if (swPersist) {
        const { getItem, NOTIFICATION_CLICKED_KEY } = swPersist;
        const clickedEvent = await getItem(NOTIFICATION_CLICKED_KEY);
        if (clickedEvent) {
          const { from, url } = clickedEvent;
          trackMixpanel({
            type: EventTypes.PUSH_NOTIFICATION_CLICKED,
            payload: clickedEvent,
          });
          const currentUrl = new URL(location.href);
          if (from === 'navigate') {
            const clickedEventUrl = new URL(url, 'https://swag.live');
            if (
              currentUrl.pathname !== clickedEventUrl.pathname ||
              currentUrl.search !== clickedEventUrl.search
            )
              push(url);
          } else if (from === 'openWindow') {
            const clickedEventUrl = new URL(url);
            if (currentUrl.href !== clickedEventUrl.href) push(url);
          }
        }
      }
    } catch (error) {
      getSentry().then(({ withScope, captureMessage }) => {
        withScope(scope => {
          scope.setTag('record', 'push-notification-clicked-events');
          captureMessage(error);
        });
      });
    } finally {
      if (swPersist) {
        const { removeItem, NOTIFICATION_CLICKED_KEY } = swPersist;
        await removeItem(NOTIFICATION_CLICKED_KEY);
      }
    }
  }, [push]);

  // after login
  useEffect(() => {
    if (isAuthed) {
      if (shouldSetFcmToken()) {
        log('get new fcm token after login');
        setFcmToken();
      }
      if (shouldSetPushyToken()) {
        log('get new pushy token after login');
        setPushyToken();
      }

      if (isSafari)
        visibilityManager.current = new VisibilityManager({
          visibleListener: handlePermission,
        });
    }
    return () => {
      visibilityManager.current?.unbindListeners();
    };
  }, [isAuthed]);

  // after login, and get new fcmToken or new pushyToken
  useEffect(() => {
    if (isAuthed && (fcmToken || pushyToken)) {
      log('submit push notification token', { fcmToken, pushyToken });
      submitPushNotificationToken({ fcmToken, pushyToken });
    }
  }, [isAuthed, fcmToken, pushyToken]);

  useEffect(() => {
    handlePermission();
  }, [permission]);

  useEffect(() => {
    if (prevPushyAddId.current !== pushyAppId) {
      if (shouldSetPushyToken()) setPushyToken();
      prevPushyAddId.current = pushyAppId;
    }
  }, [pushyAppId]);

  useEffect(() => {
    handlePushNotificationReceivedEvents();
    handlePushNotificationClickedEvent();
  }, []);

  return null;
};

PushNotificationHandler.propTypes = {
  isAuthed: PropTypes.bool,
  fcmToken: PropTypes.string,
  pushyToken: PropTypes.string,
  permission: PropTypes.string,
  pushyAppId: PropTypes.string,
  submitPushNotificationToken: PropTypes.func,
  setFcmToken: PropTypes.func,
  setPushyToken: PropTypes.func,
  push: PropTypes.func,
};

export default PushNotificationHandler;
